import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';

const DEFAULT_CURRENCY_DECIMALS = 2;
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

function getCurrencyPrecision(currency) {
  if (!currency) return DEFAULT_CURRENCY_DECIMALS;

  return CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;
}

function getCurrencyRate(shopCurrency, activeCurrency) {
  if (!shopCurrency || !activeCurrency || shopCurrency === activeCurrency) {
    return 1;
  }

  const currencyRate = Number.parseFloat(window.Shopify?.currency?.rate);
  return Number.isFinite(currencyRate) && currencyRate > 0 ? currencyRate : 1;
}

function formatRemaining(remainingMinorUnits, moneyFormat, activeCurrency) {
  try {
    return formatMoney(remainingMinorUnits, moneyFormat, activeCurrency);
  } catch (_error) {
    const precision = getCurrencyPrecision(activeCurrency);
    const divisor = Math.pow(10, precision);

    return (remainingMinorUnits / divisor).toFixed(precision);
  }
}

class CartThresholdBarComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#scheduleRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleRender();
  }

  #handleDialogOpen = () => {
    if (!this.closest('cart-drawer-component')) return;
    this.#scheduleRender();
  };

  #scheduleRender() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#render();
      });
    });
  }

  #render() {
    const config = this.#getConfig();
    if (!config) return;

    const validTiers = this.#getValidTiers(config);
    if (validTiers.length === 0) return;

    const highestTier = validTiers[validTiers.length - 1];
    const progressPercent = highestTier.targetMinorUnits > 0
      ? Math.min((config.cartSubtotalMinorUnits / highestTier.targetMinorUnits) * 100, 100)
      : 0;

    const messages = [];

    this.#setFill(progressPercent);
    this.#setMarker(validTiers);

    for (const tier of validTiers) {
      if (config.cartSubtotalMinorUnits >= tier.targetMinorUnits) {
        if (tier.successMessage) {
          messages.push(tier.successMessage);
        }

        continue;
      }

      if (tier.ongoingMessage) {
        const remainingMinorUnits = Math.max(tier.targetMinorUnits - config.cartSubtotalMinorUnits, 0);
        const formattedRemaining = formatRemaining(remainingMinorUnits, config.moneyFormat, config.activeCurrency);
        const placeholder = '[remaining]';
        const placeholderIndex = tier.ongoingMessage.indexOf(placeholder);

        if (placeholderIndex >= 0) {
          messages.push({
            before: tier.ongoingMessage.slice(0, placeholderIndex),
            remaining: formattedRemaining,
            after: tier.ongoingMessage.slice(placeholderIndex + placeholder.length),
          });
        } else {
          messages.push(tier.ongoingMessage);
        }
      }

      break;
    }

    this.#renderMessages(messages);
    this.hidden = false;
    this.removeAttribute('hidden');
    this.style.display = 'flex';
  }

  #getConfig() {
    const cartSubtotalMinorUnits = Number.parseInt(this.dataset.cartSubtotal ?? '', 10);
    const shopCurrency = this.dataset.shopCurrency || '';
    const activeCurrency = this.dataset.activeCurrency || shopCurrency;
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const tierOneTarget = Number.parseInt(this.dataset.tierOneTarget ?? '', 10);
    const tierTwoTarget = Number.parseInt(this.dataset.tierTwoTarget ?? '', 10);

    if (!Number.isFinite(cartSubtotalMinorUnits) || !Number.isFinite(tierOneTarget) || tierOneTarget <= 0) {
      return null;
    }

    return {
      activeCurrency,
      cartSubtotalMinorUnits,
      moneyFormat,
      shopCurrency,
      tiers: [
        {
          ongoingMessage: this.dataset.tierOneOngoingMessage || '',
          successMessage: this.dataset.tierOneSuccessMessage || '',
          targetDollars: tierOneTarget,
        },
        {
          ongoingMessage: this.dataset.tierTwoOngoingMessage || '',
          successMessage: this.dataset.tierTwoSuccessMessage || '',
          targetDollars: Number.isFinite(tierTwoTarget) ? tierTwoTarget : 0,
        },
      ],
    };
  }

  #getValidTiers(config) {
    const currencyRate = getCurrencyRate(config.shopCurrency, config.activeCurrency);
    const activeCurrencyPrecision = getCurrencyPrecision(config.activeCurrency);
    const multiplier = Math.pow(10, activeCurrencyPrecision);

    const tiers = config.tiers
      .filter((tier) => Number.isFinite(tier.targetDollars) && tier.targetDollars > 0)
      .map((tier) => ({
        ...tier,
        targetMinorUnits: Math.round(tier.targetDollars * currencyRate * multiplier),
      }))
      .filter((tier) => tier.targetMinorUnits > 0);

    if (tiers.length < 2) return tiers;

    return tiers[1].targetMinorUnits > tiers[0].targetMinorUnits ? tiers : [tiers[0]];
  }

  #renderMessages(messages) {
    const messageContainer = this.querySelector('[data-cart-threshold-messages]');
    if (!(messageContainer instanceof HTMLElement)) return;

    messageContainer.replaceChildren();

    for (const message of messages) {
      const messageElement = document.createElement('p');
      messageElement.className = 'cart-threshold-bar__message';

      if (typeof message === 'string') {
        messageElement.textContent = message;
        messageContainer.append(messageElement);
        continue;
      }

      const beforeText = document.createTextNode(message.before || '');
      const remainingAmount = document.createElement('strong');
      remainingAmount.className = 'cart-threshold-bar__message-amount';
      remainingAmount.textContent = message.remaining || '';
      const afterText = document.createTextNode(message.after || '');

      messageElement.append(beforeText, remainingAmount, afterText);
      messageContainer.append(messageElement);
    }
  }

  #setFill(progressPercent) {
    const fill = this.querySelector('[data-cart-threshold-fill]');
    if (!(fill instanceof HTMLElement)) return;

    fill.style.width = `${Math.max(progressPercent, 0)}%`;
  }

  #setMarker(tiers) {
    const marker = this.querySelector('[data-cart-threshold-marker]');
    if (!(marker instanceof HTMLElement)) return;

    if (tiers.length < 2) {
      marker.hidden = true;
      marker.style.removeProperty('left');
      return;
    }

    const highestTier = tiers[tiers.length - 1];
    const markerPercent = Math.min((tiers[0].targetMinorUnits / highestTier.targetMinorUnits) * 100, 100);

    marker.hidden = false;
    marker.style.left = `${markerPercent}%`;
  }
}

if (!customElements.get('cart-threshold-bar-component')) {
  customElements.define('cart-threshold-bar-component', CartThresholdBarComponent);
}
